import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useGameStore } from '../store/gameStore';

/**
 * ReconnectingModal — surfaces a recoverable WebSocket drop to the
 * player while NetClient.tryReconnect is in flight.
 *
 * Without this UI, a player on a flaky network sees the snake suddenly
 * stop responding (server stopped pushing patches), assumes the game
 * crashed, and closes the tab. They never realize an automatic reconnect
 * was 4 seconds away from succeeding. The disconnected-bucket on the
 * admin dashboard is currently > 90 %; a large slice of that is
 * recoverable drops we're silently failing on.
 *
 * Visibility rule:
 *   - gameMode === 'live'        (we're meant to be in a live match)
 *   - netStatus === 'connecting' (reconnect is in flight)
 *   AND
 *   - we've previously been 'connected'  (so this isn't the FRESH
 *     join — that's the MatchmakingGate's job)
 *
 * On 'disconnected' (all 5 reconnect attempts failed) we swap to a
 * "back to home" CTA so the player isn't trapped on a dead session.
 */
export function ReconnectingModal({ onHome }: { onHome?: () => void }) {
  const gameMode = useGameStore((s) => s.gameMode);
  const netStatus = useGameStore((s) => s.netStatus);
  // Once we've been connected at least once, treat any future
  // 'connecting' as a RECONNECT (vs the initial fresh join, which the
  // MatchmakingGate handles).
  const [wasEverConnected, setWasEverConnected] = useState(false);
  // Tick-by-tick elapsed counter so the player sees movement on the
  // panel (not a static "Reconnecting…" string for 22 s).
  const [elapsedS, setElapsedS] = useState(0);

  useEffect(() => {
    if (netStatus === 'connected') setWasEverConnected(true);
  }, [netStatus]);

  useEffect(() => {
    if (netStatus !== 'connecting' || !wasEverConnected) {
      setElapsedS(0);
      return;
    }
    setElapsedS(0);
    const t = window.setInterval(() => setElapsedS((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [netStatus, wasEverConnected]);

  const showReconnecting = gameMode === 'live' &&
                           netStatus === 'connecting' &&
                           wasEverConnected;
  const showDisconnected = gameMode === 'live' &&
                           netStatus === 'disconnected' &&
                           wasEverConnected;

  return (
    <AnimatePresence>
      {(showReconnecting || showDisconnected) && (
        <motion.div
          className="absolute inset-0 z-40 flex items-center justify-center pointer-events-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.18 } }}
          exit={{ opacity: 0, transition: { duration: 0.15 } }}
          style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.85) 70%)' }}
        >
          <motion.div
            initial={{ scale: 0.94, y: 6, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 420, damping: 28 }}
            className="hud-panel relative rounded-2xl w-[340px] overflow-hidden"
            dir="rtl"
          >
            <div className="px-7 pt-7 pb-6 text-center">
              {showReconnecting ? (
                <>
                  <div className="text-[10px] uppercase tracking-[0.4em] text-amber-300/85 mb-3">
                    Reconnecting
                  </div>
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-14 h-14 rounded-full border-2 border-white/15 border-t-cyan-300 animate-spin" />
                  </div>
                  <div className="text-base font-semibold text-white mb-2">
                    Connection dropped
                  </div>
                  <div className="text-[12px] text-white/65 leading-relaxed mb-3">
                    We're restoring your seat with your mass and rank — nothing
                    to do on your end. Your shield is preserved.
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 tabular-nums">
                    Attempt {Math.min(5, Math.floor(elapsedS / 4) + 1)} of 5 · {elapsedS}s
                  </div>
                </>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-[0.4em] text-rose-300/85 mb-3">
                    Connection lost
                  </div>
                  <div className="text-2xl font-bold text-white mb-2">
                    Reconnect failed
                  </div>
                  <div className="text-[12px] text-white/65 leading-relaxed mb-5">
                    The connection was too unstable. Head home and try again.
                  </div>
                  <button
                    onClick={onHome}
                    autoFocus
                    className="px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white text-[11px] uppercase tracking-[0.25em] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                  >
                    Back home
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
